import { LocalDate, LocalDateModel } from '../../../../../models';
import {
  ClosedPeriodModel,
  TruckLoadingType,
  WorkingHoursCalendarModel,
  WorkingHoursViewCalendarModel,
} from '../../../../administration/hub';
import { defineSlotsColumnPositions } from '../../utils/calendar.utils';
import { AllocationCalendarNonWorkingHoursSlot } from './allocation-calendar-non-working-hours-slot.model';
import {
  AllocationCalendarClosedPeriod,
  AllocationCalendarNonWorkingHours,
} from './allocation-calendar-non-working-hours.model';
import { AllocationCalendarSlot } from './allocation-calendar-slot.model';
import { DatepickerRangeModel } from '../../../../../../common/models/datepicker.model';
import { TruckSizeUnion } from '../../../../../../common/constants/truck-size';
import { TruckSizeMappingModel } from '../../../../administration/dock/models/truck-size-mapping.model';

export interface AllocationCalendarDockModel {
  dockId: number;
  compartmentId: number;
  compartmentName: string;
  closedDays: ClosedPeriodModel[];
  dockName: string;
  in: boolean;
  out: boolean;
  loadingTypes: TruckLoadingType[];
  truckSizes: TruckSizeUnion[];
  truckSizeMappings: TruckSizeMappingModel[];
  overbooking: number;
  compartmentTimezone: string;
  slots: AllocationCalendarSlot[];
  workingHoursView: WorkingHoursViewCalendarModel;
  nonWorkingHoursSlots: AllocationCalendarNonWorkingHoursSlot[];
  minServiceTime: number;
  maxServiceTime: number;
}

export class AllocationCalendarDock implements AllocationCalendarDockModel {
  dockId: number;
  compartmentId: number;
  compartmentName: string;
  closedDays: ClosedPeriodModel[];
  dockName: string;
  in: boolean;
  out: boolean;
  loadingTypes: TruckLoadingType[];
  truckSizes: TruckSizeUnion[];
  truckSizeMappings: TruckSizeMappingModel[] = [];
  overbooking: number;
  compartmentTimezone: string;
  slots: AllocationCalendarSlot[];
  workingHoursView: WorkingHoursViewCalendarModel;
  nonWorkingHoursSlots: AllocationCalendarNonWorkingHoursSlot[] = [];
  minServiceTime: number;
  maxServiceTime: number;

  private nonWorkingHours: AllocationCalendarNonWorkingHours[] = [];

  constructor(dock: AllocationCalendarDock, timeFrame: DatepickerRangeModel, oneSlotPxHeight: number) {
    Object.assign(this, dock);

    const rawSlots: AllocationCalendarSlot[] = [...dock.slots]
      .sort(
        (a: AllocationCalendarSlot, b: AllocationCalendarSlot) =>
          a.timeFrame?.start?.timestamp - b.timeFrame?.end?.timestamp,
      )
      .map((slot: AllocationCalendarSlot) => new AllocationCalendarSlot(slot, timeFrame, oneSlotPxHeight));

    defineSlotsColumnPositions(rawSlots);

    this.slots = rawSlots.sort(
      (a: AllocationCalendarSlot, b: AllocationCalendarSlot) => a.slotPosition.column - b.slotPosition.column,
    );

    this.initNonWorkingHours(timeFrame);
    this.addClosedDaysToNonWorkingHours();
    this.initNonWorkingHoursSlots(oneSlotPxHeight, timeFrame);
  }

  private initNonWorkingHours(timeFrame: DatepickerRangeModel): void {
    this.nonWorkingHours = [];

    if (!this.workingHoursView.workingHours.length) {
      return;
    }

    const { workingHours, validFrom } = this.workingHoursView;
    const firstWorkingHour = workingHours[0];
    const lastWorkingHour = workingHours[workingHours.length - 1];

    const { offset, ...rest }: LocalDateModel = firstWorkingHour?.start || new LocalDateModel();

    const firstNonWorkingTimeLocalDate: LocalDateModel = {
      ...rest,
      offset,
      timestamp: validFrom?.timestamp,
    };

    const lastNonWorkingTimeLocalDate: LocalDateModel = {
      ...rest,
      offset,
      timestamp: lastWorkingHour.end.timestamp,
    };

    workingHours.reduce((acc: LocalDate, current: WorkingHoursCalendarModel) => {
      this.nonWorkingHours.push(new AllocationCalendarNonWorkingHours(acc, current.start));

      return current.end;
    }, firstNonWorkingTimeLocalDate);

    this.nonWorkingHours.push({
      start: LocalDateModel.getLocalDate(lastNonWorkingTimeLocalDate),
      end: new Date(timeFrame.end),
    });
  }

  private addClosedDaysToNonWorkingHours(): void {
    this.closedDays.forEach((closedDay: ClosedPeriodModel) => {
      this.nonWorkingHours.push(
        new AllocationCalendarClosedPeriod(closedDay.period.start, closedDay.period.end, closedDay.name),
      );
    });
  }

  private initNonWorkingHoursSlots(oneSlotPxHeight: number, timeFrame: DatepickerRangeModel): void {
    this.nonWorkingHoursSlots = [];
    this.nonWorkingHours.forEach((nonWorkingSlot: AllocationCalendarNonWorkingHours) => {
      this.nonWorkingHoursSlots.push(
        new AllocationCalendarNonWorkingHoursSlot(nonWorkingSlot, timeFrame, oneSlotPxHeight),
      );
    });
  }
}
